'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label, Switch } from '@quadminds/mindkit';
import {
    mindyAdminRole,
    mindyConsultantRole,
    mindyGestRole,
    MindyGroupedRoles,
    MindyKnowledgePermissions,
    MindyLogisticPermissions,
    mindyManagerRole,
    mindyOperationalRole,
    MindyRolesPermissions,
    MindyStandardRoles,
    MindyUsersPermissions,
} from '@quadminds/mindy-shared';
import { StandardUserRoles } from '@quadminds/shared/commons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface Permission {
    id: MindyStandardRoles;
    isOpen: boolean;
    handleClose: (value: boolean) => void;
}

/**
 * Define el tipo de permiso basado en las enumeraciones existentes.
 */
type PermissionDefinition =
    | MindyUsersPermissions
    | MindyRolesPermissions
    | MindyKnowledgePermissions
    | MindyLogisticPermissions;

const DefaultRoleDefinition: Record<MindyStandardRoles, PermissionDefinition[]> = {
    [MindyStandardRoles.ADMIN]: mindyAdminRole,
    [MindyStandardRoles.MANAGER]: mindyManagerRole,
    [MindyStandardRoles.GUEST]: mindyGestRole,
    [MindyStandardRoles.CONSULTANT]: mindyConsultantRole,
    [MindyStandardRoles.OPERATIONAL]: mindyOperationalRole,
};
function getDefaultRoleDefinition(roles: (MindyStandardRoles | StandardUserRoles)[]): PermissionDefinition[] {
    return roles
        .filter((role) => Object.keys(DefaultRoleDefinition).includes(role))
        .flatMap((role) => DefaultRoleDefinition[role as MindyStandardRoles]);
}

const GroupedRolesDefinition = {
    [MindyStandardRoles.ADMIN]: getDefaultRoleDefinition(MindyGroupedRoles.admin.roles),
    [MindyStandardRoles.MANAGER]: getDefaultRoleDefinition(MindyGroupedRoles.manager.roles),
    [MindyStandardRoles.GUEST]: getDefaultRoleDefinition(MindyGroupedRoles.guest.roles),
    [MindyStandardRoles.CONSULTANT]: getDefaultRoleDefinition(MindyGroupedRoles.consultant.roles),
    [MindyStandardRoles.OPERATIONAL]: getDefaultRoleDefinition(MindyGroupedRoles.operational.roles),
};

type PermissionAdminType = MindyUsersPermissions | MindyRolesPermissions;

type PermissionChatType = MindyKnowledgePermissions | MindyLogisticPermissions;

const UserRoleMap: Record<MindyStandardRoles, string> = {
    [MindyStandardRoles.ADMIN]: 'administrator',
    [MindyStandardRoles.MANAGER]: 'manager',
    [MindyStandardRoles.GUEST]: 'guest',
    [MindyStandardRoles.CONSULTANT]: 'consultant',
    [MindyStandardRoles.OPERATIONAL]: 'operational',
};

const RoleGrid: { admin: Record<string, PermissionAdminType[]>; chat: Record<string, PermissionChatType[]> } = {
    admin: {
        users: [
            MindyUsersPermissions.ReadUser,
            MindyUsersPermissions.CreateUser,
            MindyUsersPermissions.UpdateUser,
            MindyUsersPermissions.DeleteUser,
        ],
        roles: [
            MindyRolesPermissions.ReadRole,
            MindyRolesPermissions.CreateRole,
            MindyRolesPermissions.UpdateRole,
            MindyRolesPermissions.DeleteRole,
        ],
    },
    chat: {
        knowledge: [
            MindyKnowledgePermissions.AskKnowledge,
            MindyKnowledgePermissions.ReadKnowledge,
            MindyKnowledgePermissions.CreateKnowledge,
            MindyKnowledgePermissions.UpdateKnowledge,
            MindyKnowledgePermissions.DeleteKnowledge,
        ],
        logistic: [MindyLogisticPermissions.AskLogistic],
    },
};
export const Permissions: FC<Permission> = ({ id, isOpen, handleClose }) => {
    const currentRole = GroupedRolesDefinition[id];
    const { t } = useTranslation();
    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-[840px] max-h-[440px] bg-[#263238] text-white border-slate-700">
                <DialogHeader>
                    <DialogTitle className="font-poppins font-semibold text-[20px] leading-6">
                        {t('settings.permissions.title')} -{' '}
                        <span className="text-purple-400">{t(`settings.roles.${UserRoleMap[id]}`)}</span>
                    </DialogTitle>
                    <p className="text-slate-400 text-sm leading-6 m-0">{t('settings.permissions.description')}</p>
                </DialogHeader>
                <div className="border-t border-[#37474F] w-full" />
                <div>
                    {/* Role Name Input */}
                    <div>
                        <Label htmlFor="roleName" className="text-white text-[14px] leading-6 font-roboto">
                            {t('settings.permissions.rolName')}
                        </Label>
                        <Input
                            id="roleName"
                            value={t(`settings.roles.${UserRoleMap[id]}`)}
                            className="bg-transparent border-slate-600 text-white placeholder:text-slate-400 h-10 text-base"
                            disabled={true}
                        />
                    </div>

                    {/* Permissions Table */}
                    <div className="bg-[#00000026] p-2 rounded mt-4">
                        {/* Table Header */}
                        <div className="grid grid-cols-6 gap-4 pb-4">
                            <h3 className="text-[14px] leading-6 font-semibold font-poppins text-white">
                                {t('settings.permissions.permissions')}
                            </h3>
                            <div className="text-center text-slate-300 font-medium font-roboto text-[14px] leading-6">
                                {t('settings.permissions.ask')}
                            </div>
                            <div className="text-center text-slate-300 font-medium font-roboto text-[14px] leading-6">
                                {t('settings.permissions.view')}
                            </div>
                            <div className="text-center text-slate-300 font-medium font-roboto text-[14px] leading-6">
                                {t('settings.permissions.create')}
                            </div>
                            <div className="text-center text-slate-300 font-medium font-roboto text-[14px] leading-6">
                                {t('settings.permissions.edit')}
                            </div>
                            <div className="text-center text-slate-300 font-medium font-roboto text-[14px] leading-6">
                                {t('settings.permissions.delete')}
                            </div>
                        </div>

                        {/* ADMINISTRACIÓN Section */}
                        <div>
                            <h4 className="font-roboto font-normal text-[11px] pl-2 leading-6">
                                {t('settings.permissions.admin')}
                            </h4>
                            {Object.entries(RoleGrid.admin).map(([key, values], index) => (
                                <div key={key} className="grid grid-cols-6 items-center even:bg-[#263238] pl-2 h-8">
                                    <div className="text-white font-medium text-[15px] leading-6 font-roboto">
                                        {t(`settings.permissions.${key}`)}
                                    </div>
                                    <div className="flex justify-center" />
                                    {values.map((permission) => (
                                        <div className="flex justify-center">
                                            <Switch
                                                id={permission}
                                                checked={currentRole.includes(permission)}
                                                className="data-[state=checked]:bg-purple-600 h-[18px] w-[33px]"
                                                classNameThumbs="h-3.5 w-3.5 data-[state=checked]:translate-x-3.5"
                                                disabled={true}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-[#37474F] w-full" />
                        {/* CONVERSACIÓN Section */}
                        <div>
                            <h4 className="font-roboto font-normal text-[11px] leading-6 pl-2">
                                {t('settings.permissions.conversation')}
                            </h4>
                            {Object.entries(RoleGrid.chat).map(([key, values], index) => (
                                <div key={key} className="grid grid-cols-6  items-center even:bg-[#263238] pl-2 h-8">
                                    <div className="text-white font-medium text-[15px] leading-6 font-roboto">
                                        {t(`settings.permissions.${key}`)}
                                    </div>
                                    {values.map((permission, index) => (
                                        <div key={index} className="flex justify-center">
                                            <Switch
                                                checked={currentRole.includes(permission)}
                                                className="data-[state=checked]:bg-purple-600 h-[18px] w-[33px]"
                                                classNameThumbs="h-3.5 w-3.5 data-[state=checked]:translate-x-3.5"
                                                disabled={true}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default Permissions;
